import { type FunctionComponent, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory, useLocation } from 'react-router'
import {
  MenuBase,
  MenuItem,
  MenuHeader,
  Divider,
  NavigationBar,
} from '@retailer-platform/shared-components/src/tds'
import { ChevronDownIcon, ChevronUpIcon } from '@instacart/ids-core'
import { AccountManagementMyAccountModal } from '@retailer-platform/domain-account-management'
import { useOptionalPartnerContext } from '../../../utils/contexts/partner/PartnerContext.hooks'
import { useAdminControls } from '../../../utils/contexts/admin-controls/AdminControlsContext'
import { useCurrentAccountContext } from '../../../utils/current-account/CurrentAccountContext'
import useAccessControl from '../../../legacy/components/AccessControl/useAccessControl'
import { Permission } from '../../../legacy/common/types'
import { useIsReadOnly } from '../../utils/access/useIsReadOnly'
import {
  useHasWriteAccessForCurrentPartner,
  useIsPartnerWriteAccessRequired,
} from '../../utils/access/usePartnerWriteAccess.hooks'
import { useCoreContext } from '../../../utils/core/RPPCoreContext'
import { useAbsoluteRouteMatch } from '../../../utils/routing/useAbsoluteRouteMatch.hooks'
import { url as resolveUrlPattern } from '../../../utils/parsing/url'
import { URL_LOGOUT } from '../../../utils/routing/routes'
import SelectPartner from '../../common/select-partner/SelectPartner'
import { AdminControlModal } from '../admin-controls-modal/AdminControlsModal'
import { setSettingsBackHref } from './settingsBackHrefStore'
import { useTopBarMenu } from './TopBarMenuContext'
import { useNavHierarchySettingsV3 } from './useNavHierarchySettingsV3'

const CHEVRON_SIZE = 16

const SETTINGS_ROUTE = 'partner-account-management-list'

const useRouteHref = (routeName: string): string | undefined => {
  const { routesByName } = useCoreContext()
  const routeMatch = useAbsoluteRouteMatch()

  return useMemo(() => {
    const pattern = routesByName[routeName]
    if (!pattern) return undefined
    return resolveUrlPattern(pattern, (routeMatch?.params as Record<string, string>) ?? {})
  }, [routeName, routesByName, routeMatch?.params])
}

export const TdsPartnerMenu: FunctionComponent = () => {
  const intl = useIntl()
  const history = useHistory()
  const location = useLocation()
  const routeMatch = useAbsoluteRouteMatch()
  const { indexedRoutes } = useNavHierarchySettingsV3()
  const { isOpen, setIsOpen } = useTopBarMenu('partner')
  const [showPartnerSelector, setShowPartnerSelector] = useState(false)
  const [showManageAccount, setShowManageAccount] = useState(false)
  const partnerContext = useOptionalPartnerContext()
  const { openAdminControls } = useAdminControls()
  const { account } = useCurrentAccountContext()
  const hasAccess = useAccessControl()
  const isInternalAdmin = hasAccess({ permissions: Permission.AdminPanel })

  const settingsHref = useRouteHref(SETTINGS_ROUTE)

  const isReadOnly = useIsReadOnly()
  const isPartnerWriteAccessRequired = useIsPartnerWriteAccessRequired()
  const hasWriteAccessForCurrentPartner = useHasWriteAccessForCurrentPartner()
  const showReadOnly = Boolean(
    account?.isInternal &&
    isPartnerWriteAccessRequired &&
    (isReadOnly || !hasWriteAccessForCurrentPartner)
  )

  const baseLabel =
    partnerContext?.partnerName ?? intl.formatMessage({ id: 'navV2.userSettings.instacartAdmin' })
  const triggerLabel = showReadOnly
    ? `${baseLabel}${intl.formatMessage({ id: 'app.nav.partnerMenu.readOnlySuffix' })}`
    : baseLabel

  const Chevron = isOpen ? ChevronUpIcon : ChevronDownIcon

  const trigger = (
    <NavigationBar.Button
      aria-label={intl.formatMessage({ id: 'app.nav.partnerMenu.label' })}
      trailingIcon={<Chevron size={CHEVRON_SIZE} fill="currentColor" />}
      onPress={() => setIsOpen(!isOpen)}
    >
      {triggerLabel}
    </NavigationBar.Button>
  )

  return (
    <>
      <MenuBase target={trigger} isOpen={isOpen} onMenuStateChange={setIsOpen} position="bottomEnd">
        {partnerContext ? (
          <>
            <MenuHeader title={partnerContext.partnerName} />
            <MenuItem
              text={intl.formatMessage({ id: 'app.nav.partnerMenu.switchPartner' })}
              onClick={() => {
                setIsOpen(false)
                setShowPartnerSelector(true)
              }}
            />
            <MenuItem
              text={intl.formatMessage({ id: 'app.nav.partnerMenu.settings' })}
              onClick={() => {
                setIsOpen(false)
                if (!settingsHref) return
                const isAlreadyInSettings = Boolean(
                  routeMatch?.route && indexedRoutes[routeMatch.route]
                )
                if (!isAlreadyInSettings) {
                  setSettingsBackHref(`${location.pathname}${location.search}`)
                }
                history.push(settingsHref)
              }}
            />
          </>
        ) : (
          <MenuItem
            text={intl.formatMessage({ id: 'navV2.userSettings.selectPartner' })}
            onClick={() => {
              setIsOpen(false)
              setShowPartnerSelector(true)
            }}
          />
        )}
        <Divider />
        <MenuHeader title={account.givenName} />
        <MenuItem text={account.email} disabled />
        <MenuItem
          text={intl.formatMessage({ id: 'navV2.userSettings.manageAccount' })}
          onClick={() => {
            setIsOpen(false)
            setShowManageAccount(true)
          }}
        />
        {isInternalAdmin && (
          <MenuItem
            text={intl.formatMessage({ id: 'app.admin.nav.links.adminControls' })}
            onClick={() => {
              setIsOpen(false)
              openAdminControls()
            }}
          />
        )}
        <MenuItem text={intl.formatMessage({ id: 'logout' })} href={URL_LOGOUT} />
      </MenuBase>
      {showPartnerSelector && <SelectPartner handleClose={() => setShowPartnerSelector(false)} />}
      <AccountManagementMyAccountModal
        isOpen={showManageAccount}
        onClose={() => setShowManageAccount(false)}
      />
      {isInternalAdmin && <AdminControlModal />}
    </>
  )
}
